import type { PreconditionBundle } from '@sr-test-wizard/shared';

interface Props {
  bundle: PreconditionBundle;
  pushing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function statusLabel(s: 'todo' | 'done' | 'skipped'): string {
  return s === 'todo' ? 'TODO' : s === 'done' ? 'DONE' : 'SKIPPED';
}

/**
 * Confirms the push diff before we touch Jira. Lists every staged change
 * with from -> to so the tester can spot a mis-click. Pushing is the only
 * Jira-mutating action in this phase, so the modal is a hard gate rather
 * than a soft "Are you sure?" toast.
 */
export function PushConfirmModal({ bundle, pushing, onCancel, onConfirm }: Props) {
  const pending = bundle.items
    .map((item) => {
      const stage = bundle.staged[item.id];
      if (!stage?.status || stage.status === item.status) return null;
      return { item, from: item.status, to: stage.status as 'todo' | 'done' | 'skipped' };
    })
    .filter((row): row is { item: typeof bundle.items[number]; from: 'todo' | 'done' | 'skipped'; to: 'todo' | 'done' | 'skipped' } => row !== null);

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ maxWidth: 640, width: '100%', maxHeight: '85vh', overflow: 'auto' }}
      >
        <h3 style={{ marginTop: 0 }}>Push to {bundle.tpKey}</h3>
        <p className="muted small" style={{ marginTop: 0 }}>
          Review the {pending.length} staged change{pending.length === 1 ? '' : 's'} below. Pushing rewrites the Smart Checklist on the Jira task.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {pending.map(({ item, from, to }) => (
            <div
              key={item.id}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                background: 'var(--bg-elev-2)',
              }}
            >
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <span className="badge small">{statusLabel(from)}</span>
                <span className="muted small">-&gt;</span>
                <span className={`badge ${to === 'done' ? 'good' : to === 'skipped' ? '' : 'warn'} small`}>
                  {statusLabel(to)}
                </span>
                {item.group && <span className="muted small">{item.group}</span>}
              </div>
              <div style={{ marginTop: 4 }}>{item.label}</div>
            </div>
          ))}
        </div>
        <div className="row" style={{ marginTop: 16, justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={pushing}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={pushing || pending.length === 0}>
            {pushing ? 'Pushing...' : `Push ${pending.length} change${pending.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
